import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export interface Item {
    id: string;
    name: string;
    quantity_fluid_type1?: number;
    quantity_fluid_type2?: number;
    quantity_container?: number;
    remove?: boolean;
}

export interface InventoryExcelOptions {
    date?: Date;
    performedBy?: string;
    checkedBy?: string;
    fileName?: string;
}

const formatInventoryDate = (date: Date): string => {
    const pad = (value: number) => String(value).padStart(2, "0");

    return (
        `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()} ` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}hod`
    );
};

const formatNumber = (value: number) =>
    value.toLocaleString("en-US");

export const exportInventoryToExcel = async (
    items: readonly Item[],
    options?: InventoryExcelOptions
) => {
    const {
        date = new Date(),
        performedBy = "",
        checkedBy = "",
        fileName = "inventura.xlsx",
    } = options ?? {};

    const visibleItems = items.filter(item => !item.remove);

    const totalFluidType1 = visibleItems.reduce(
        (sum, item) => sum + (Number(item.quantity_fluid_type1) ?? 0),
        0
    );

    const totalFluidType2 = visibleItems.reduce(
        (sum, item) => sum + (Number(item.quantity_fluid_type2) ?? 0),
        0
    );

    const totalContainers = visibleItems.reduce(
        (sum, item) => sum + (Number(item.quantity_container) ?? 0),
        0
    );

    // ============================================================
    // WORKBOOK
    // ============================================================

    const workbook = new ExcelJS.Workbook();

    workbook.creator = "Inventory";
    workbook.created = new Date();

    const worksheet = workbook.addWorksheet("Inventura", {
        pageSetup: {
            paperSize: 9, // A4
            orientation: "portrait",
            fitToPage: true,
            fitToWidth: 0,
            fitToHeight: 0,
            margins: {
                left: 0.3,
                right: 0.3,
                top: 0.4,
                bottom: 0.4,
                header: 0.2,
                footer: 0.2,
            },
        },
    });

    worksheet.properties.defaultRowHeight = 18;

    // ============================================================
    // SLOUPCE
    // ============================================================

    worksheet.getColumn("A").width = 38;
    worksheet.getColumn("B").width = 18;
    worksheet.getColumn("C").width = 18;
    worksheet.getColumn("D").width = 18;

    // ============================================================
    // POMOCNÉ STYLY
    // ============================================================

    const border: Partial<ExcelJS.Borders> = {
        top: {
            style: "thin",
            color: { argb: "FF000000" },
        },
        bottom: {
            style: "thin",
            color: { argb: "FF000000" },
        },
        left: {
            style: "thin",
            color: { argb: "FF000000" },
        },
        right: {
            style: "thin",
            color: { argb: "FF000000" },
        },
    };

    const center: Partial<ExcelJS.Alignment> = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
    };

    const left: Partial<ExcelJS.Alignment> = {
        horizontal: "left",
        vertical: "middle",
    };

    const numberFormat = '#,##0;[Red]-#,##0;0';

    // ============================================================
    // ŘÁDEK 1 - NADPIS
    // ============================================================

    worksheet.mergeCells("A1:D1");

    const title = worksheet.getCell("A1");

    title.value =
        "Inventura / fyzická kontrola stavu skladových zásob";

    title.font = {
        name: "Arial",
        size: 14,
        bold: true,
    };

    title.alignment = center;

    worksheet.getRow(1).height = 25;

    // ============================================================
    // ŘÁDEK 2 - DATUM
    // ============================================================

    worksheet.mergeCells("A2:D2");

    const subtitle = worksheet.getCell("A2");

    subtitle.value =
        `odmrazovacích kapalin ke dni ${formatInventoryDate(date)}`;

    subtitle.font = {
        name: "Arial",
        size: 12,
        bold: true,
    };

    subtitle.alignment = center;

    worksheet.getRow(2).height = 23;

    // ============================================================
    // ŘÁDEK 3 - PRÁZDNÝ
    // ============================================================

    worksheet.getRow(3).height = 10;

    // ============================================================
    // ŘÁDEK 4 - HLAVIČKA
    // ============================================================

    worksheet.getCell("A4").value = "";

    worksheet.getCell("B4").value =
        "DEICING FLUID TYPE I";

    worksheet.getCell("C4").value =
        "DEICING FLUID TYPE II";

    worksheet.getCell("D4").value =
        "IBC";

    ["A4", "B4", "C4", "D4"].forEach(address => {
        const cell = worksheet.getCell(address);

        cell.font = {
            name: "Arial",
            size: 10,
            bold: true,
        };

        cell.alignment = center;
        cell.border = border;
    });

    worksheet.getRow(4).height = 35;

    // ============================================================
    // POLOŽKY
    // ============================================================

    let currentRow = 5;

    visibleItems.forEach(item => {
        const row = worksheet.getRow(currentRow);

        row.height = 28;

        // Název
        const nameCell = row.getCell(1);

        nameCell.value = item.name;

        nameCell.font = {
            name: "Arial",
            size: 10,
        };

        nameCell.alignment = left;
        nameCell.border = border;

        // --------------------------------------------------------
        // TYPE I
        // --------------------------------------------------------

        const type1Cell = row.getCell(2);

        type1Cell.value =
            item.quantity_fluid_type1 ?? 0;

        type1Cell.font = {
            name: "Arial",
            size: 10,
        };

        type1Cell.alignment = center;
        type1Cell.border = border;
        type1Cell.numFmt = numberFormat;

        // --------------------------------------------------------
        // TYPE II
        // --------------------------------------------------------

        const type2Cell = row.getCell(3);

        type2Cell.value =
            item.quantity_fluid_type2 ?? 0;

        type2Cell.font = {
            name: "Arial",
            size: 10,
        };

        type2Cell.alignment = center;
        type2Cell.border = border;
        type2Cell.numFmt = numberFormat;

        // --------------------------------------------------------
        // IBC count
        // --------------------------------------------------------

        const type3Cell = row.getCell(4);

        type3Cell.value =
            item.quantity_container ?? 0;

        type3Cell.font = {
            name: "Arial",
            size: 10,
        };

        type3Cell.alignment = center;
        type3Cell.border = border;
        type3Cell.numFmt = numberFormat;

        currentRow++;
    });

    // ============================================================
    // CELKEM
    // ============================================================

    const totalRow = worksheet.getRow(currentRow);

    totalRow.height = 23;

    totalRow.getCell(1).value =
        "Celkem";

    totalRow.getCell(2).value =
        formatNumber(totalFluidType1);

    totalRow.getCell(3).value =
        formatNumber(totalFluidType2);

    totalRow.getCell(4).value =
        formatNumber(totalContainers);

    for (let column = 1; column <= 4; column++) {
        const cell = totalRow.getCell(column);

        cell.font = {
            name: "Arial",
            size: 10,
            bold: true,
        };

        cell.border = border;

        cell.alignment =
            column === 1
                ? left
                : center;
    }

    // totalRow.getCell(2).numFmt = numberFormat;
    // totalRow.getCell(3).numFmt = numberFormat;

    currentRow++;

    // ============================================================
    // PRÁZDNÝ ŘÁDEK
    // ============================================================

    worksheet.getRow(currentRow).height = 10;

    currentRow++;

    // ============================================================
    // IBC KONTEJNERY POČET
    // ============================================================

    // const containerRow = worksheet.getRow(currentRow);

    // containerRow.height = 22;

    // containerRow.getCell(1).value =
    //     "IBC kontejnery počet";

    // containerRow.getCell(1).font = {
    //     name: "Arial",
    //     size: 10,
    //     bold: true,
    // };

    // containerRow.getCell(1).alignment = left;

    // // Hodnota podle vzoru v pravém sloupci
    // containerRow.getCell(3).value =
    //     formatNumber(totalContainers);

    // containerRow.getCell(3).font = {
    //     name: "Arial",
    //     size: 10,
    //     bold: true,
    // };

    // containerRow.getCell(3).alignment = center;
    // containerRow.getCell(3).numFmt = numberFormat;

    // ============================================================
    // MEZERA PŘED PODPISY
    // ============================================================

    //currentRow += 3;

    // ============================================================
    // KONTROLU PROVEDL
    // ============================================================

    const performedRow = currentRow;

    worksheet.getCell(`A${performedRow}`).value =
        "Kontrolu provedl:";

    worksheet.getCell(`A${performedRow}`).font = {
        name: "Arial",
        size: 10,
    };

    worksheet.getCell(`A${performedRow}`).alignment = left;

    worksheet.mergeCells(
        `B${performedRow}:C${performedRow}`
    );

    worksheet.getCell(`B${performedRow}`).value =
        performedBy;

    worksheet.getCell(`B${performedRow}`).font = {
        name: "Arial",
        size: 10,
    };

    worksheet.getCell(`B${performedRow}`).alignment = left;

    // ============================================================
    // KONTROLOVAL
    // ============================================================

    currentRow += 2;

    const checkedRow = currentRow;

    worksheet.getCell(`A${checkedRow}`).value =
        "Kontroloval / ověřil:";

    worksheet.getCell(`A${checkedRow}`).font = {
        name: "Arial",
        size: 10,
    };

    worksheet.getCell(`A${checkedRow}`).alignment = left;

    worksheet.mergeCells(
        `B${checkedRow}:C${checkedRow}`
    );

    worksheet.getCell(`B${checkedRow}`).value =
        checkedBy;

    worksheet.getCell(`B${checkedRow}`).font = {
        name: "Arial",
        size: 10,
    };

    worksheet.getCell(`B${checkedRow}`).alignment = left;

    // ============================================================
    // TISK
    // ============================================================

    worksheet.pageSetup.printArea =
        `A1:D${checkedRow + 1}`;

    worksheet.pageSetup.horizontalCentered = true;
    worksheet.pageSetup.verticalCentered = false;

    // ============================================================
    // EXPORT
    // ============================================================

    const buffer =
        await workbook.xlsx.writeBuffer();

    const blob = new Blob(
        [buffer],
        {
            type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
    );

    saveAs(blob, fileName);
};